import { queryKeys, useHttpClient } from '@repo/api-client/react';
import {
  OrganizationSchema,
  type Organization,
  type OrganizationListItem,
} from '@repo/api-client/dtos';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { putImage, type UploadableImage } from '../internal/imageUpload';

export interface UploadOrgLogoVariables {
  organizationId: string;
  file: UploadableImage;
}

/**
 * Multipart upload for an organisation's logo.
 *
 * Pass the picker output (`{ uri, type, name }`) and the org id; the
 * hook PUTs the file at `/orgs/:id/logo`, parses the returned org,
 * and updates both the detail and list caches so every surface that
 * shows the org gets the new `logoUrl` immediately.
 */
export function useUploadOrgLogo() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ organizationId, file }: UploadOrgLogoVariables) =>
      putImage(http, `/orgs/${organizationId}/logo`, file, OrganizationSchema),
    onSuccess: (organization) => {
      queryClient.setQueryData(
        queryKeys.organizations.detail(organization.id),
        organization,
      );
      queryClient.setQueryData<{ items: OrganizationListItem[] }>(
        queryKeys.organizations.all(),
        (cache) =>
          cache
            ? {
                ...cache,
                items: cache.items.map((o) =>
                  o.id === organization.id ? { ...o, ...organization } : o,
                ),
              }
            : cache,
      );
    },
  });

  return {
    uploadLogo: mutation.mutateAsync as (
      vars: UploadOrgLogoVariables,
    ) => Promise<Organization>,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
